// client/src/components/admin/PlanSelector.jsx
import React, { useState } from 'react';

const PLANS = {
  basic: {
    label: 'Basic',
    price: 29,
    color: '#E3F2FD',
    textColor: '#1565C0',
    emoji: '📋',
    features: [
      '✅ Онлайн меню',
      '✅ QR коды для столов',
      '✅ Печать QR',
      '❌ Система заказов',
      '❌ Приложение официантов',
      '❌ Банкеты',
      '❌ Telegram бот',
      '❌ Доставка',
    ],
  },
  pro: {
    label: 'Pro',
    price: 59,
    color: '#F3E5F5',
    textColor: '#7B1FA2',
    emoji: '⚡',
    features: [
      '✅ Онлайн меню',
      '✅ QR коды для столов',
      '✅ Печать QR',
      '✅ Система заказов',
      '✅ Приложение официантов',
      '✅ Банкеты',
      '✅ Telegram бот',
      '❌ Доставка',
    ],
  },
  business: {
    label: 'Business',
    price: 129,
    color: '#FFF8E1',
    textColor: '#F57F17',
    emoji: '🚀',
    features: [
      '✅ Онлайн меню',
      '✅ QR коды для столов',
      '✅ Печать QR',
      '✅ Система заказов',
      '✅ Приложение официантов',
      '✅ Банкеты',
      '✅ Telegram бот',
      '✅ Страница доставки',
      '✅ Click / Payme',
      '✅ Свой курьер',
    ],
  },
};

export default function PlanSelector({ restaurant, api, onSuccess, onCancel }) {
  const [selectedPlan, setSelectedPlan] = useState(restaurant.plan || 'basic');
  const [months, setMonths] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const currentPlan = PLANS[selectedPlan];

  const expiresDate = () => {
    const d = new Date();
    d.setMonth(d.getMonth() + months);
    return d.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      await api.patch(`/api/superadmin/restaurants/${restaurant.id}/plan`, {
        plan: selectedPlan,
        months: Number(months),
      });
      onSuccess(`✅ План "${PLANS[selectedPlan].label}" подключён до ${expiresDate()}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        <div style={s.header}>
          <div>
            <div style={s.title}>Подписка — {restaurant.name}</div>
            <div style={s.subtitle}>Текущий план: {PLANS[restaurant.plan]?.label || 'Basic'}</div>
          </div>
          <button style={s.closeBtn} onClick={onCancel}>✕</button>
        </div>

        {error && <div style={s.error}>{error}</div>}

        <div style={s.plansRow}>
          {Object.entries(PLANS).map(([key, plan]) => (
            <button
              key={key}
              style={{
                ...s.planCard,
                backgroundColor: plan.color,
                border: selectedPlan === key
                  ? `2px solid ${plan.textColor}`
                  : '2px solid transparent',
                transform: selectedPlan === key ? 'scale(1.02)' : 'scale(1)',
              }}
              onClick={() => setSelectedPlan(key)}
            >
              <div style={s.planEmoji}>{plan.emoji}</div>
              <div style={{ ...s.planLabel, color: plan.textColor }}>
                {plan.label}
              </div>
              <div style={{ ...s.planPrice, color: plan.textColor }}>
                ${plan.price}/мес
              </div>
            </button>
          ))}
        </div>

        <div style={s.featuresBox}>
          <div style={s.featuresTitle}>
            {currentPlan.emoji} {currentPlan.label} — что включено:
          </div>
          <div style={s.featuresList}>
            {currentPlan.features.map((f, i) => (
              <div key={i} style={s.featureItem}>{f}</div>
            ))}
          </div>
        </div>

        <div style={s.monthsRow}>
          <div style={s.label}>Срок подписки:</div>
          <div style={s.monthsBtns}>
            {[1, 3, 6, 12].map((m) => (
              <button
                key={m}
                style={{
                  ...s.monthBtn,
                  backgroundColor: months === m ? '#000' : '#F0F0F0',
                  color: months === m ? '#fff' : '#333',
                }}
                onClick={() => setMonths(m)}
              >
                {m} мес
              </button>
            ))}
          </div>
        </div>

        <div style={s.summary}>
          <div style={s.summaryRow}>
            <span>Сумма:</span>
            <span style={s.summaryPrice}>
              ${currentPlan.price * months}
            </span>
          </div>
          <div style={s.summaryRow}>
            <span>Активен до:</span>
            <span style={s.summaryDate}>{expiresDate()}</span>
          </div>
        </div>

        <div style={s.actions}>
          <button
            style={s.saveBtn}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Сохранение...' : `Подключить ${currentPlan.label}`}
          </button>
          <button style={s.cancelBtn} onClick={onCancel}>
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '16px',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '20px',
    padding: '24px',
    width: '100%',
    maxWidth: '480px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '700',
  },
  subtitle: {
    fontSize: '13px',
    color: '#888',
    marginTop: '2px',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#888',
    padding: '0',
  },
  error: {
    padding: '10px',
    borderRadius: '10px',
    backgroundColor: '#FFEBEE',
    color: '#E85D4A',
    fontSize: '13px',
    marginBottom: '16px',
  },
  plansRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
  },
  planCard: {
    flex: 1,
    padding: '12px 8px',
    borderRadius: '12px',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'all 0.15s ease',
  },
  planEmoji: {
    fontSize: '24px',
    marginBottom: '4px',
  },
  planLabel: {
    fontSize: '14px',
    fontWeight: '700',
    marginBottom: '2px',
  },
  planPrice: {
    fontSize: '12px',
    fontWeight: '500',
  },
  featuresBox: {
    backgroundColor: '#F8F8F8',
    borderRadius: '12px',
    padding: '14px',
    marginBottom: '16px',
  },
  featuresTitle: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '10px',
  },
  featuresList: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '4px',
  },
  featureItem: {
    fontSize: '12px',
    color: '#444',
  },
  monthsRow: {
    marginBottom: '16px',
  },
  label: {
    fontSize: '13px',
    color: '#666',
    fontWeight: '500',
    marginBottom: '8px',
  },
  monthsBtns: {
    display: 'flex',
    gap: '8px',
  },
  monthBtn: {
    flex: 1,
    padding: '8px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    transition: 'all 0.15s ease',
  },
  summary: {
    backgroundColor: '#F8F8F8',
    borderRadius: '12px',
    padding: '14px',
    marginBottom: '16px',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    marginBottom: '4px',
  },
  summaryPrice: {
    fontWeight: '700',
    fontSize: '18px',
    color: '#000',
  },
  summaryDate: {
    fontWeight: '600',
    color: '#2E7D32',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  saveBtn: {
    flex: 1,
    padding: '14px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: '#000',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  cancelBtn: {
    padding: '14px 16px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: '#F0F0F0',
    cursor: 'pointer',
    fontSize: '14px',
  },
};
